// Office navigation presentation only. Civil definitions and RBAC stay authoritative.
import { useAuth } from "@/lib/auth";
import { NAV_GROUPS, HEALTH_SAFETY_GROUPS, WORKSPACES, filterNavGroups, breadcrumbFor } from "@/constants/nav";

const OFFICE_ROLES = ["super_admin", "admin", "office", "quantity_surveyor"];
const leaves = groups => groups.flatMap(g => g.items.flatMap(i => i.children || [i]));
const sourceItems = leaves([...NAV_GROUPS, ...HEALTH_SAFETY_GROUPS]);
const item = (testId, changes = {}) => ({ ...sourceItems.find(i => i.testId === testId), ...changes });
const GROUPS = [
  { id: "home", label: "Overview", items: [item("nav-overview", { label: "Dashboard" })] },
  { id: "work", label: "Work", items: [
    item("nav-crew-planner", { label: "Schedule", to: "/app/crew-planner?view=day" }),
    item("nav-jobs"), item("nav-materials"), item("nav-clients", { label: "Customers" }),
  ] },
  { id: "commercial", label: "Sales", items: [item("nav-quotes"),
    item("nav-billing", { label: "Invoices", to: "/app/invoicing/customer-invoices" })] },
  { id: "people", label: "Team", items: [item("nav-team"), item("nav-timesheets")] },
  { id: "contacts", label: "Tools", items: [
    item("nav-hs-forms", { label: "Forms", to: "/app/safety-qa/forms" }), item("nav-company-documents", { label: "Documents" }),
  ] },
  { id: "more", label: "More", items: [
    ...NAV_GROUPS.find(g => g.id === "more").items.filter(i => i.id === "reports"),
    { to: "/app/branding", label: "Settings", icon: WORKSPACES[0].icon,
      roles: ["super_admin", "admin", "office"], testId: "nav-trades-settings" },
  ] },
];

export function useNavigationPresentation() {
  const { user, operatingProfile, refresh } = useAuth();
  const office = OFFICE_ROLES.includes(user?.role);
  const context = operatingProfile?.context;
  const known = context && context.org_id === user?.org_id;
  // Preserve the same tenant's last-known projection on a transient failure.
  // No guessed Civil office navigation while an org projection is unavailable.
  return { user, refresh,
    pending: !!(office && user?.org_id && !known),
    trades: !!(office && known && context.mode === "TRADES_SERVICES"),
  };
}

export function tradeGroups(user) {
  const visible = i => i.roles.includes(user.role) && (!i.flag || user.role !== "field" || !!user[i.flag]);
  return GROUPS.map(g => ({ ...g, items: g.items.map(i => {
    if (!i.children) return visible(i) ? i : null;
    const children = i.children.filter(visible);
    return children.length === 1 ? { ...children[0] } : children.length ? { ...i, children } : null;
  }).filter(Boolean) })).filter(g => g.items.length);
}
export const navigationGroups = (user, workspace, trades) => trades ? tradeGroups(user) : filterNavGroups(user, workspace);
export const tradeLinks = user => leaves(tradeGroups(user));
export const TRADE_WORKSPACE = { ...WORKSPACES[0], id: "trades", label: "Trades & Services", home: "/app", desc: "Day planning, jobs and customer work." };

export function navigationBreadcrumb(pathname, user, trades) {
  if (!trades) return breadcrumbFor(pathname);
  const aliases = { "/app/customer-invoices": "/app/invoicing/customer-invoices", "/app/forms": "/app/safety-qa/forms", "/app/team": "/app/team-hub", "/app/timesheets": "/app/time" };
  let path = pathname;
  for (const [from, to] of Object.entries(aliases)) if (path === from || path.startsWith(from + "/")) { path = to + path.slice(from.length); break; }
  let best;
  for (const group of tradeGroups(user)) for (const entry of leaves([group])) {
    const to = entry.to.split("?")[0];
    if ((path === to || (!entry.end && path.startsWith(to + "/"))) && (!best || to.length > best.length)) best = { length: to.length, trail: [group.label, entry.label] };
  }
  return best ? best.trail.filter((s, i, a) => i === 0 || s !== a[i - 1]) : breadcrumbFor(pathname);
}

// Keep safety reporting available; hiding advanced navigation is not disabling work.
export const tradeActionVisible = to => ["/app/jobs", "/app/crew-planner", "/app/quotes", "/app/customer-invoices", "/app/timesheets", "/app/team", "/app/safety"].some(base => to.split("?")[0] === base || to.startsWith(base + "/"));
export const tradeActionLabel = action => action.to.startsWith("/app/crew-planner") ? "Open schedule" : action.label;
export const tradeHubKeys = { "/app/invoicing": ["customer-invoices"], "/app/team-hub": ["team"], "/app/time": ["timesheets"], "/app/safety-qa": ["forms"] };
